import { CalendarIcon } from '@jouzen/ecom-components/icons/CalendarIcon';
import { CircularOutlineCheckIcon } from '@jouzen/ecom-components/icons/CircularOutlineCheckIcon';
import { LockV2Icon } from '@jouzen/ecom-components/icons/LockV2Icon';
import { MobileCompatibilityIcon } from '@jouzen/ecom-components/icons/MobileCompatibilityIcon';
import { StarIcon } from '@jouzen/ecom-components/icons/StarIcon';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { type RichTranslationValues, useLocale } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';

import type { LandingPageHeroProps } from '@/app/components/LandingPage/LandingPageHero';
import LandingPageHero from '@/app/components/LandingPage/LandingPageHero';
import type { MotionProps } from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import useMembershipPricing from '@/app/hooks/useMembershipPricing';

const tagTransitionDuration = 0.3;

const cyclingTagsContainerMotionProps: MotionProps = {
  className:
    'relative inline-block overflow-hidden rounded-lg bg-sandstone-200',
  layout: true,
  transition: {
    layout: {
      duration: tagTransitionDuration,
      ease: 'easeInOut',
    },
  },
};

const Tag = ({
  Icon,
  msgKey,
  tValues,
}: {
  readonly Icon: SVGIconComponent;
  readonly msgKey: MessageKey;
  readonly tValues?: RichTranslationValues;
}) => {
  return (
    <div
      className="inline-flex items-center px-4 py-2 text-sandstone-500"
      data-cy="membership-hero-tag"
    >
      <Icon className="mr-2" width={16} height={16} />
      <Typography variant="body-small" className="leading-snug tracking-tight">
        <RichText messageKey={msgKey} tValues={tValues} />
      </Typography>
    </div>
  );
};

const MembershipHero = () => {
  const locale = useLocale();
  const membershipPricingTValues = useMembershipPricing({
    footnoteSymbol: '*',
  });

  const cyclingTags = [
    <Tag
      key="membership_redesign_hero_tag_1"
      Icon={StarIcon}
      msgKey="membership_redesign_hero_tag_1"
    />,
    <Tag
      key="membership_redesign_hero_tag_2"
      Icon={MobileCompatibilityIcon}
      msgKey="membership_redesign_hero_tag_2"
    />,
    <Tag
      key="membership_redesign_hero_tag_3"
      Icon={CircularOutlineCheckIcon}
      msgKey="membership_redesign_hero_tag_3"
    />,
    <Tag
      key="membership_redesign_hero_tag_4"
      Icon={CalendarIcon}
      msgKey="membership_redesign_hero_tag_4"
      tValues={membershipPricingTValues}
    />,
    <Tag
      key="membership_redesign_hero_tag_5"
      Icon={LockV2Icon}
      msgKey="membership_redesign_hero_tag_5"
    />,
  ];

  // Text is long enough in languages other than English that it wraps to multiple lines for some tags,
  // which makes the animations look strange, so only show cycling tags for English locale
  const cyclingTagsProps: LandingPageHeroProps['cyclingTagsProps'] =
    locale === 'en'
      ? {
          containerMotionProps: cyclingTagsContainerMotionProps,
          animateItemLayout: 'position', // prevents contents of tag from distorting during layout animation
          cycleDuration: 5000,
          transitionDuration: tagTransitionDuration,
          elements: cyclingTags,
        }
      : null;

  return (
    <section data-cy="membership-hero">
      <LandingPageHero
        title="membership_redesign_hero_title"
        copy="membership_redesign_hero_copy"
        copyTValues={membershipPricingTValues}
        backgroundImage={{
          localizedAlt: 'pop_gen4_long_term_health_img_alt',
          localizedSrcSet: [
            {
              media: '(max-width: 1023px)',
              src: 'membership_redesign_hero_mobile_image',
            },
            {
              media: '(min-width: 1024px)',
              src: 'membership_redesign_hero_desktop_image',
            },
          ],
        }}
        backgroundProps={{
          color: 'sandstone-200',
          gradients: [],
        }}
        textColor="text-sandstone-200"
        eyebrow="oura_membership"
        eyebrowProps={{ className: 'uppercase' }}
        layoutGridClassName="pb-8! lg:pb-15! pt-16! lg:pt-24!"
        titleWrapperClassName="p-0!"
        cyclingTagsProps={cyclingTagsProps}
      />
    </section>
  );
};

export default MembershipHero;
